import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Button,
  VStack,
  Heading,
  Textarea,
  Select,
  Flex,
  FormErrorMessage,
} from "@chakra-ui/react";
import FileUpload from "../FileUpload";

function RoomInfoStep({
  formData: roomData,
  onNext,
  onBack,
  isLastRoom,
  onAddRoom,
  onNextStep,
  onRemoveRoom,
  currentStepIndex,
  isRoomStep,
  onUpdateStepData,
  justAddedRoom,
  submissionId,
}) {
  const [stepData, setStepData] = useState({
    roomName: "",
    sleepingTemperature: "",
    roomDimensions: "",
    roomVideo: [],
    fireplaceType: "none",
    fireplaceVent: "",
    heatingCooling: "",
    otherAirSystems: "",
  });
  const [errors, setErrors] = useState({});
  const topRef = useRef(null);

  // Scroll to top when a new room is added
  useEffect(() => {
    if (justAddedRoom && topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [justAddedRoom]);

  const fireplaceTypes = [
    { value: "", label: "Select Fireplace Type" },
    { value: "woodburning", label: "Woodburning" },
    { value: "gas", label: "Gas" },
    { value: "none", label: "None" },
  ];

  const fireplaceVents = [
    { value: "", label: "Select Vent Type" },
    { value: "open", label: "Open Vent" },
    { value: "direct", label: "Direct Vent" },
    { value: "unknown", label: "Unknown" },
    { value: "n/a", label: "N/A (No Fireplace)" },
  ];

  useEffect(() => {
    if (justAddedRoom) {
      setStepData({
        roomName: "",
        sleepingTemperature: "",
        roomDimensions: "",
        roomVideo: [],
        fireplaceType: "none",
        fireplaceVent: "",
        heatingCooling: "",
        otherAirSystems: "",
        submissionId: roomData.submissionId || "unknown",
      });
    } else if (roomData.roomName) {
      setStepData((prevData) => ({
        ...prevData,
        ...roomData,
        fireplaceType:
          roomData.fireplaceType !== undefined
            ? roomData.fireplaceType
            : "none",
      }));
    }
  }, [roomData, justAddedRoom]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedData = { ...stepData, [name]: value };
    setStepData(updatedData);
    onUpdateStepData(updatedData);
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const handleFileUpload = (fieldName, files) => {
    const updatedData = { ...stepData, [fieldName]: files };
    setStepData(updatedData);
    onUpdateStepData(updatedData);
    if (errors[fieldName]) {
      setErrors({ ...errors, [fieldName]: null });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!stepData.roomName) newErrors.roomName = "Room Name is required";
    if (!stepData.sleepingTemperature)
      newErrors.sleepingTemperature =
        "Preferred Sleeping Temperature is required";
    if (!stepData.roomDimensions)
      newErrors.roomDimensions = "Room Dimensions are required";
    if (!stepData.roomVideo || stepData.roomVideo.length === 0)
      newErrors.roomVideo = "Video of your room is required";
    if (!stepData.fireplaceType)
      newErrors.fireplaceType = "Fireplace type is required";
    if (stepData.fireplaceType !== "none" && !stepData.fireplaceVent)
      newErrors.fireplaceVent = "Fireplace vent type is required";
    if (!stepData.heatingCooling)
      newErrors.heatingCooling = "Heating and Cooling description is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextClick = () => {
    if (validate()) {
      if (isLastRoom) {
        onNextStep();
      } else {
        onNext();
      }
    }
  };

  const handleAddRoomClick = () => {
    if (validate()) {
      onAddRoom();
    }
  };

  const isFirstRoom = currentStepIndex === 2;
  const roomIdentifier =
    stepData.roomName || (isRoomStep ? `${currentStepIndex - 1}` : "");

  return (
    <Box>
      <div ref={topRef} />
      <Heading as="h2" size="lg" mb={6}>
        Room Information {roomIdentifier ? `(${roomIdentifier})` : ""}
      </Heading>
      <VStack spacing={4}>
        <FormControl id="roomName" isRequired isInvalid={!!errors.roomName}>
          <FormLabel>Room Name</FormLabel>
          <Input
            name="roomName"
            value={stepData.roomName || ""}
            onChange={handleChange}
          />
          <FormErrorMessage>{errors.roomName}</FormErrorMessage>
        </FormControl>

        <FormControl
          id="sleepingTemperature"
          isRequired
          isInvalid={!!errors.sleepingTemperature}
        >
          <FormLabel>Preferred Sleeping Temperature (°F)</FormLabel>
          <Input
            name="sleepingTemperature"
            value={stepData.sleepingTemperature || ""}
            onChange={handleChange}
            type="number"
          />
          <FormErrorMessage>{errors.sleepingTemperature}</FormErrorMessage>
        </FormControl>

        <FormControl
          id="roomDimensions"
          isRequired
          isInvalid={!!errors.roomDimensions}
        >
          <FormLabel>
            Room Dimensions (LxWxH, include adjacent spaces/sketch details)
          </FormLabel>
          <Textarea
            name="roomDimensions"
            value={stepData.roomDimensions || ""}
            onChange={handleChange}
          />
          <FormErrorMessage>{errors.roomDimensions}</FormErrorMessage>
        </FormControl>

        <FormControl id="roomVideo" isRequired isInvalid={!!errors.roomVideo}>
          <FormLabel>
            Video of your room (doors, windows, fireplace, finishes)
          </FormLabel>
          <FileUpload
            fieldName={`roomVideo_${currentStepIndex - 2}`}
            onFilesUpload={(keys) => handleFileUpload("roomVideo", keys)}
            allowMultiple={true}
            accept="video/*"
            submissionId={submissionId}
          />
          <FormErrorMessage>{errors.roomVideo}</FormErrorMessage>
        </FormControl>

        <FormControl
          id="fireplaceType"
          isRequired
          isInvalid={!!errors.fireplaceType}
        >
          <FormLabel>
            If you have a fireplace, is it woodburning or gas?
          </FormLabel>
          <Select
            name="fireplaceType"
            value={stepData.fireplaceType || ""}
            onChange={handleChange}
          >
            {fireplaceTypes.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          <FormErrorMessage>{errors.fireplaceType}</FormErrorMessage>
        </FormControl>

        {stepData.fireplaceType !== "none" && (
          <FormControl
            id="fireplaceVent"
            isRequired
            isInvalid={!!errors.fireplaceVent}
          >
            <FormLabel>Do you know if its open vent or direct vent?</FormLabel>
            <Select
              name="fireplaceVent"
              value={stepData.fireplaceVent || ""}
              onChange={handleChange}
            >
              {fireplaceVents.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.fireplaceVent}</FormErrorMessage>
          </FormControl>
        )}

        <FormControl
          id="heatingCooling"
          isRequired
          isInvalid={!!errors.heatingCooling}
        >
          <FormLabel>How is your room heated and cooled?</FormLabel>
          <Textarea
            name="heatingCooling"
            value={stepData.heatingCooling || ""}
            onChange={handleChange}
          />
          <FormErrorMessage>{errors.heatingCooling}</FormErrorMessage>
        </FormControl>

        <FormControl id="otherAirSystems" isInvalid={!!errors.otherAirSystems}>
          <FormLabel>
            Please describe any other systems that move air (HRV/ERV,
            humidification, etc.)?
          </FormLabel>
          <Textarea
            name="otherAirSystems"
            value={stepData.otherAirSystems || ""}
            onChange={handleChange}
          />
          <FormErrorMessage>{errors.otherAirSystems}</FormErrorMessage>
        </FormControl>

        {/* Navigation buttons for Room Step */}
        <VStack width="100%" spacing={3} mt={4}>
          {isLastRoom && (
            <>
              <Button
                onClick={handleAddRoomClick}
                variant="outline"
                colorScheme="teal"
                width="100%"
              >
                Add Another Room
              </Button>
              {!isFirstRoom && (
                <Button
                  colorScheme="red"
                  variant="outline"
                  onClick={() => onRemoveRoom(currentStepIndex - 2)}
                  width="100%"
                  mt={0}
                >
                  Remove Room
                </Button>
              )}
            </>
          )}
          <Flex width="100%" justify="space-between">
            <Button onClick={onBack} variant="outline" width="48%">
              Back
            </Button>
            {isLastRoom ? (
              <Button
                bg="brand.100"
                color="white"
                onClick={handleNextClick}
                width="48%"
              >
                Next Step
              </Button>
            ) : (
              <Button
                bg="brand.100"
                color="white"
                onClick={handleNextClick}
                width="48%"
              >
                Next Room
              </Button>
            )}
          </Flex>
        </VStack>
      </VStack>
    </Box>
  );
}

export default RoomInfoStep;
